import { supabase } from '../lib/supabase';

// Anexo D.4.12 — Integración realista de Plausible (capa ligera, privacy-first).
// Se activa SÓLO si existe la variable de entorno VITE_PLAUSIBLE_DOMAIN.
// De lo contrario la función es un NO-OP (aunque sigue logeando en dev y
// persistiendo en localStorage igual que antes).
declare global {
  interface Window {
    plausible?: (
      event: string,
      options?: { props?: Record<string, unknown>; u?: string }
    ) => void;
  }
}

const PLAUSIBLE_DOMAIN = (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined)?.trim();
const PLAUSIBLE_SRC = (import.meta.env.VITE_PLAUSIBLE_SRC as string | undefined)?.trim() ||
  'https://plausible.io/js/script.js';

let plausibleScriptInjected = false;

const injectPlausibleScript = () => {
  if (plausibleScriptInjected) return;
  if (!PLAUSIBLE_DOMAIN) return;
  if (typeof document === 'undefined') return;
  if (document.querySelector(`script[data-plausible-domain="${PLAUSIBLE_DOMAIN}"]`)) {
    plausibleScriptInjected = true;
    return;
  }
  try {
    const s = document.createElement('script');
    s.defer = true;
    s.setAttribute('data-domain', PLAUSIBLE_DOMAIN);
    s.setAttribute('data-plausible-domain', PLAUSIBLE_DOMAIN);
    s.src = PLAUSIBLE_SRC;
    s.onerror = () => console.warn('[analytics] No se pudo cargar Plausible desde', PLAUSIBLE_SRC);
    document.head.appendChild(s);
    plausibleScriptInjected = true;
  } catch (err) {
    console.warn('[analytics] Error al inyectar Plausible:', err);
  }
};

injectPlausibleScript();

export interface AnalyticsData {
  date: string;
  profile_views: number;
  post_views: number;
  post_likes: number;
  post_comments: number;
  post_shares: number;
  followers_gained: number;
  followers_lost: number;
}

interface TopPost {
  id: string;
  title?: string;
  views_count?: number;
  likes_count?: number;
  comments_count?: number;
  created_at?: string;
  media_files?: Array<{ url: string; type: string }>;
}

export interface AnalyticsSummary {
  total_profile_views: number;
  total_post_views: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  net_followers: number;
  average_engagement_rate: number;
  top_performing_posts: TopPost[];
}

export const analyticsService = {
  trackEvent(eventName: string, properties?: Record<string, unknown>) {
    // Log to console for development/verification
    console.log(`📊 Analytics Event: ${eventName}`, properties);

    // Store in localStorage for verification
    const eventLog = JSON.parse(localStorage.getItem('analytics_events') || '[]');
    eventLog.unshift({
      id: crypto.randomUUID(),
      event: eventName,
      properties,
      timestamp: new Date().toISOString()
    });
    if (eventLog.length > 50) eventLog.pop();
    localStorage.setItem('analytics_events', JSON.stringify(eventLog));

    // Enviar a Plausible SI Y SÓLO SI la variable de entorno está presente.
    if (PLAUSIBLE_DOMAIN && typeof window !== 'undefined') {
      const send = () => {
        try {
          if (typeof window.plausible === 'function') {
            window.plausible(eventName, { props: properties ?? {} });
          } else {
            // Fallback: endpoint raw `/api/event` de Plausible para los casos
            // en que el script no se ha terminado de cargar aún.
            void fetch(`${new URL(PLAUSIBLE_SRC).origin}/api/event`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: eventName,
                domain: PLAUSIBLE_DOMAIN,
                url: window.location.href,
                props: properties ?? {},
              }),
            }).catch(() => {
              /* ignore */
            });
          }
        } catch {
          // no-op
        }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', send, { once: true });
      } else {
        send();
      }
    }
  },

  async getUserAnalytics(options?: {
    startDate?: string;
    endDate?: string;
    period?: 'week' | 'month' | 'quarter' | 'year';
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let startDate = options?.startDate;
    const endDate = options?.endDate || new Date().toISOString().split('T')[0];

    // Set default date range based on period
    if (!startDate && options?.period) {
      const end = new Date();
      const start = new Date();

      switch (options.period) {
        case 'week':
          start.setDate(end.getDate() - 7);
          break;
        case 'month':
          start.setMonth(end.getMonth() - 1);
          break;
        case 'quarter':
          start.setMonth(end.getMonth() - 3);
          break;
        case 'year':
          start.setFullYear(end.getFullYear() - 1);
          break;
      }

      startDate = start.toISOString().split('T')[0];
    }

    if (!startDate) {
      // Default to last 30 days
      const start = new Date();
      start.setDate(start.getDate() - 30);
      startDate = start.toISOString().split('T')[0];
    }

    // Generate date range and calculate real-time analytics
    const days = this.generateDateRange(startDate, endDate);
    const analyticsData = await this.calculateRealTimeAnalytics(user.id, days);

    return analyticsData;
  },

  async getAnalyticsSummary(period: 'week' | 'month' | 'quarter' | 'year' = 'month') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get real-time data directly from database
    const [postsData, likesData, commentsData, followsData, topPosts] = await Promise.all([
      // Get user's posts in period
      supabase
        .from('posts')
        .select('id, views_count, created_at')
        .eq('author_id', user.id)
        .gte('created_at', this.getPeriodStartDate(period).toISOString()),

      // Get likes on user's posts in period
      supabase
        .from('likes')
        .select('post_id, created_at, posts!inner(author_id)')
        .eq('posts.author_id', user.id)
        .gte('created_at', this.getPeriodStartDate(period).toISOString()),

      // Get comments on user's posts in period
      supabase
        .from('comments')
        .select('post_id, created_at, posts!inner(author_id)')
        .eq('posts.author_id', user.id)
        .gte('created_at', this.getPeriodStartDate(period).toISOString()),

      // Get new followers in period
      supabase
        .from('follows')
        .select('created_at')
        .eq('following_id', user.id)
        .gte('created_at', this.getPeriodStartDate(period).toISOString()),

      // Get top performing posts
      supabase
        .from('posts')
        .select(`
          id,
          title,
          views_count,
          likes_count,
          comments_count,
          created_at,
          media_files (
            url,
            type
          )
        `)
        .eq('author_id', user.id)
        .order('views_count', { ascending: false })
        .limit(5)
    ]);

    const summary: AnalyticsSummary = {
      total_profile_views: 0, // Would need profile view tracking
      total_post_views: postsData.data?.reduce((sum, post) => sum + (post.views_count || 0), 0) || 0,
      total_likes: likesData.data?.length || 0,
      total_comments: commentsData.data?.length || 0,
      total_shares: 0, // Would need share tracking
      net_followers: followsData.data?.length || 0,
      average_engagement_rate: 0,
      top_performing_posts: topPosts.data || []
    };

    // Calculate engagement rate
    const totalInteractions = summary.total_likes + summary.total_comments + summary.total_shares;
    summary.average_engagement_rate = summary.total_post_views > 0
      ? (totalInteractions / summary.total_post_views) * 100
      : 0;

    return summary;
  },

  generateDateRange(startDate: string, endDate: string): string[] {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  },

  async calculateRealTimeAnalytics(userId: string, dates: string[]): Promise<AnalyticsData[]> {
    const analyticsData: AnalyticsData[] = [];

    for (const date of dates) {
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextDateStr = nextDate.toISOString().split('T')[0];

      // Get data for this specific date
      const [postsViews, likes, comments, newFollows] = await Promise.all([
        // Posts views for this date (approximate from posts created this date)
        supabase
          .from('posts')
          .select('views_count')
          .eq('author_id', userId)
          .gte('created_at', date)
          .lt('created_at', nextDateStr),

        // Likes received this date
        supabase
          .from('likes')
          .select('id, posts!inner(author_id)')
          .eq('posts.author_id', userId)
          .gte('created_at', date)
          .lt('created_at', nextDateStr),

        // Comments received this date
        supabase
          .from('comments')
          .select('id, posts!inner(author_id)')
          .eq('posts.author_id', userId)
          .gte('created_at', date)
          .lt('created_at', nextDateStr),

        // New followers this date
        supabase
          .from('follows')
          .select('id')
          .eq('following_id', userId)
          .gte('created_at', date)
          .lt('created_at', nextDateStr)
      ]);

      analyticsData.push({
        date,
        profile_views: 0, // Would need specific tracking
        post_views: postsViews.data?.reduce((sum, post) => sum + (post.views_count || 0), 0) || 0,
        post_likes: likes.data?.length || 0,
        post_comments: comments.data?.length || 0,
        post_shares: 0, // Would need specific tracking
        followers_gained: newFollows.data?.length || 0,
        followers_lost: 0, // Would need specific tracking
      });
    }

    return analyticsData;
  },

  getPeriodStartDate(period: 'week' | 'month' | 'quarter' | 'year'): Date {
    const now = new Date();
    const start = new Date();

    switch (period) {
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(now.getFullYear() - 1);
        break;
    }

    return start;
  },

  async getComparisonData(period: 'week' | 'month' | 'quarter' | 'year' = 'month') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get current period data
    const currentData = await this.getUserAnalytics({ period });

    // Get previous period data for comparison
    const end = new Date();
    const start = new Date();

    switch (period) {
      case 'week':
        start.setDate(end.getDate() - 14);
        end.setDate(end.getDate() - 7);
        break;
      case 'month':
        start.setMonth(end.getMonth() - 2);
        end.setMonth(end.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(end.getMonth() - 6);
        end.setMonth(end.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(end.getFullYear() - 2);
        end.setFullYear(end.getFullYear() - 1);
        break;
    }

    const previousData = await this.getUserAnalytics({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });

    // Calculate totals and percentage changes
    const currentTotals = {
      profile_views: currentData.reduce((sum, day) => sum + day.profile_views, 0),
      post_views: currentData.reduce((sum, day) => sum + day.post_views, 0),
      post_likes: currentData.reduce((sum, day) => sum + day.post_likes, 0),
      post_comments: currentData.reduce((sum, day) => sum + day.post_comments, 0),
    };

    const previousTotals = {
      profile_views: previousData.reduce((sum, day) => sum + day.profile_views, 0),
      post_views: previousData.reduce((sum, day) => sum + day.post_views, 0),
      post_likes: previousData.reduce((sum, day) => sum + day.post_likes, 0),
      post_comments: previousData.reduce((sum, day) => sum + day.post_comments, 0),
    };

    const calculateChange = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      current: currentTotals,
      previous: previousTotals,
      changes: {
        profile_views: calculateChange(currentTotals.profile_views, previousTotals.profile_views),
        post_views: calculateChange(currentTotals.post_views, previousTotals.post_views),
        post_likes: calculateChange(currentTotals.post_likes, previousTotals.post_likes),
        post_comments: calculateChange(currentTotals.post_comments, previousTotals.post_comments),
      },
      chartData: currentData
    };
  },
};